import axios from 'axios';
import { invalidateDashboardCache } from '../utils/dashboardCache';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshPromiseWeb = null;

const refreshAccessTokenWeb = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('NO_REFRESH_TOKEN');
  }
  const baseURL = process.env.REACT_APP_API_URL || '/api';
  const { data } = await axios.post(`${baseURL}/auth/refresh-token`, { refreshToken });
  const { accessToken, refreshToken: newRefreshToken } = data.data;

  localStorage.setItem('token', accessToken);
  if (newRefreshToken) {
    localStorage.setItem('refreshToken', newRefreshToken);
  }

  api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  return accessToken;
};

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;

    if (err.response?.status === 401 && !originalRequest._retry) {
      if (
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/refresh-token') ||
        originalRequest.url?.includes('/auth/register') ||
        originalRequest.url?.includes('/plans')
      ) {
        return Promise.reject(err);
      }

      originalRequest._retry = true;

      // Single-flight refresh lock: All concurrent 401 requests share this exact promise
      if (!refreshPromiseWeb) {
        refreshPromiseWeb = refreshAccessTokenWeb().finally(() => {
          refreshPromiseWeb = null;
        });
      }

      try {
        const newAccessToken = await refreshPromiseWeb;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshErr) {
        // Only wipe credentials and redirect if explicitly 401 (invalid/expired refresh token)
        // or missing refresh token. Do NOT wipe on network error / server downtime.
        if (refreshErr.response?.status === 401 || refreshErr.message === 'NO_REFRESH_TOKEN') {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/register')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(refreshErr);
      }
    } else if (err.response?.status === 403) {
      const data = err.response.data;
      if (data?.planLocked) {
        window.dispatchEvent(new CustomEvent('plan-locked', { detail: { reason: data.reason } }));
      } else if (data?.limitHit) {
        window.dispatchEvent(new CustomEvent('plan-limit', { detail: data }));
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register:                  (data) => api.post('/auth/register', data),
  createRegistrationOrder:   (data) => api.post('/auth/register-order', data),
  verifyRegistrationPayment: (data) => api.post('/auth/register-verify-payment', data),
  loginEmail:     (data) => api.post('/auth/login', { loginType: 'email', ...data }),
  requestOTP:     (mobile) => api.post('/auth/login', { loginType: 'mobile_request', mobile }),
  verifyOTP:      (mobile, otp) => api.post('/auth/login', { loginType: 'mobile_verify', mobile, otp }),
  getMe:          () => api.get('/auth/me'),
  updateProfile:  (data) => api.put('/auth/update-profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  deleteAccount:  () => api.delete('/auth/delete-account'),
};

export const userAPI = {
  getAll:         (params)          => api.get('/users', { params }),
  getById:        (id)              => api.get(`/users/${id}`),
  create:         (data)            => api.post('/users', data),
  update:         (id, data)        => api.put(`/users/${id}`, data),
  delete:         (id)              => api.delete(`/users/${id}`),
  resetPassword:  (id, newPassword) => api.post(`/users/${id}/reset-password`, { newPassword }),
  getBulkAvatars: (ids)             => api.get('/users/avatars', { params: { ids: ids.join(',') } }),
};

export const departmentAPI = {
  getAll:  (params)     => api.get('/departments', { params }),
  getById: (id)         => api.get(`/departments/${id}`),
  create:  (data)       => api.post('/departments', data),
  update:  (id, data)   => api.put(`/departments/${id}`, data),
  addMembers:    (id, data) => api.post(`/departments/${id}/members/add`, data),
  removeMembers: (id, data) => api.post(`/departments/${id}/members/remove`, data),
  delete:  (id)         => api.delete(`/departments/${id}`),
};

export const groupAPI = {
  getAll:        (params)       => api.get('/groups', { params }),
  getById:       (id)           => api.get(`/groups/${id}`),
  create:        (data)         => api.post('/groups', data),
  update:        (id, data)     => api.put(`/groups/${id}`, data),
  addMembers:    (id, data)     => api.post(`/groups/${id}/members/add`, data),
  removeMembers: (id, data)     => api.post(`/groups/${id}/members/remove`, data),
  delete:        (id)           => api.delete(`/groups/${id}`),
};

export const taskAPI = {
  getAll:        (params)             => api.get('/tasks', { params }),
  getById:       (id)                 => api.get(`/tasks/${id}`),
  create:        async (data)         => { const res = await api.post('/tasks', data); invalidateDashboardCache(); return res; },
  update:        async (id, data)     => { const res = await api.put(`/tasks/${id}`, data); invalidateDashboardCache(); return res; },
  delete:        async (id)           => { const res = await api.delete(`/tasks/${id}`); invalidateDashboardCache(); return res; },
  dashboard:     ()                   => api.get('/tasks/dashboard'),
  addComment:    (id, data)           => api.post(`/tasks/${id}/comments`, data),
  deleteComment: (id, commentId)      => api.delete(`/tasks/${id}/comments/${commentId}`),
  approve:       async (id, data)     => { const res = await api.patch(`/tasks/${id}/approve`, data); invalidateDashboardCache(); return res; },
  directReopen:  async (id)           => { const res = await api.post(`/tasks/${id}/direct-reopen`); invalidateDashboardCache(); return res; },
  ping:          (id)                 => api.post(`/tasks/${id}/ping`),
  // Attachments
  addAttachment:    (id, data)          => api.post(`/tasks/${id}/attachments`, data),
  deleteAttachment: (id, attId)         => api.delete(`/tasks/${id}/attachments/${attId}`),
  // Voice Notes
  addVoiceNote:     (id, data)          => api.post(`/tasks/${id}/voice-notes`, data),
  deleteVoiceNote:  (id, vnId)          => api.delete(`/tasks/${id}/voice-notes/${vnId}`),
  // Subtasks
  addSubtask:    (id, data)           => api.post(`/tasks/${id}/subtasks`, data),
  updateSubtask: (id, subtaskId, data)=> api.put(`/tasks/${id}/subtasks/${subtaskId}`, data),
  deleteSubtask: (id, subtaskId)      => api.delete(`/tasks/${id}/subtasks/${subtaskId}`),
};
export const taskTemplateAPI = {
  getAll: (params) => api.get('/task-templates', { params }),
  create: (data)   => api.post('/task-templates', data),
  delete: (id)     => api.delete(`/task-templates/${id}`),
};
export const uploadAPI = {
  uploadTaskAttachments: (formData) => api.post('/upload/task-attachment', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const deadlineExtensionAPI = {
  request: (taskId, data) => api.post(`/tasks/${taskId}/deadline-extension`, data),
  getAll:  (params)       => api.get('/tasks/deadline-requests', { params }),
  review:  (id, data)     => api.put(`/tasks/deadline-requests/${id}/review`, data),
};

export const timesheetAPI = {
  getMy:        (date)         => api.get('/timesheets/my', { params: { date } }),
  saveMy:       (data)         => api.put('/timesheets/my', data),
  getAll:       (date)         => api.get('/timesheets', { params: { date } }),
  getUser:      (userId, date) => api.get(`/timesheets/${userId}`, { params: { date } }),
  nudgePending: (date)         => api.post('/timesheets/nudge-pending', { date }),
  nudgeUser:    (userId, date) => api.post('/timesheets/nudge-user', { userId, date }),
};

export const tenantAPI = {
  get:    ()     => api.get('/tenant'),
  update: (data) => api.put('/tenant', data),
};

export const superadminAPI = {
  getStats:             ()         => api.get('/superadmin/stats'),
  getTenants:           (params)   => api.get('/superadmin/tenants', { params }),
  getTenant:            (id)       => api.get(`/superadmin/tenants/${id}`),
  updateTenant:         (id, data) => api.put(`/superadmin/tenants/${id}`, data),
  updateWhatsAppConfig: (id, data) => api.put(`/superadmin/tenants/${id}/whatsapp-config`, data),
  updateTenantPlan:     (id, data) => api.put(`/superadmin/tenants/${id}/plan`, data),
  registerTenant:       (data)     => api.post('/superadmin/tenants/register', data),
};

export const notificationAPI = {
  getAll:        (params) => api.get('/notifications', { params }),
  markAllRead:   ()       => api.put('/notifications/mark-read'),
  markRead:      (id)     => api.put(`/notifications/${id}/read`),
  updatePrefs:   (data)   => api.put('/notifications/prefs', data),
  getBroadcasts: ()       => api.get('/notifications/broadcasts'),
  sendBroadcast: (data)   => api.post('/notifications/broadcast', data),
  sendEmailBroadcast:    (data)  => api.post('/notifications/send-email', data),
  sendWhatsappBroadcast: (data)  => api.post('/notifications/send-whatsapp', data),
};

export const misAPI = {
  adminOverview: (params) => api.get('/mis/overview', { params }),
  myStats:       ()       => api.get('/mis/my'),
  presence:      ()       => api.get('/mis/presence'),
  teamPerformance: () => api.get('/mis/team-performance'),
  managerTAT:      () => api.get('/mis/manager-tat'),
};

export const reopenAPI = {
  request: (taskId, data) => api.post(`/tasks/${taskId}/reopen-request`, data),
  getAll:  (params)       => api.get('/reopen-requests', { params }),
  review:  (id, data)     => api.put(`/reopen-requests/${id}/review`, data),
  directReopen: (taskId)  => api.post(`/tasks/${taskId}/direct-reopen`),
};

export const sessionAPI = {
  getActive: () => api.get('/sessions/active'),
  getMy:     () => api.get('/sessions/me'),
  checkout:  () => api.post('/sessions/checkout'),
};

export const orgAPI = {
  getBroadcastConfig:  ()     => api.get('/tenant/broadcast-config'),
  saveBroadcastConfig: (data) => api.put('/tenant/broadcast-config', data),
  testSMS:             (mobile) => api.post('/tenant/test-sms', { mobile }),
  testEmail:           (email) => api.post('/tenant/test-email', { email }),
};


export const attendanceAPI = {
  getStatus:          ()         => api.get('/attendance/status'),
  checkIn:            ()         => api.post('/attendance/checkin'),
  checkOut:           ()         => api.post('/attendance/checkout'),
  startBreak:         (type)     => api.post('/attendance/break/start', { type }),
  endBreak:           ()         => api.post('/attendance/break/end'),
  getMyHistory:       (date)     => api.get('/attendance/my', { params: { date } }),
  getMyLeaves:        (params)   => api.get('/attendance/leave/my', { params }),
  getTeamAttendance:  (p)        => api.get('/attendance/team', { params: typeof p === 'object' ? p : { date: p } }),
  getUserAttendance:  (id, p)    => api.get(`/attendance/user/${id}`, { params: p }),
  applyLeave:      (data)   => api.post('/attendance/leave/apply', data),
  getLeaveRequests:(params) => api.get('/attendance/leave/requests', { params }),
  reviewLeave:     (id, data) => api.put(`/attendance/leave/${id}/review`, data),
  cancelLeave:     (id, data) => api.put(`/attendance/leave/${id}/cancel`, data),
  editLeave:       (id, data) => api.put(`/attendance/leave/${id}/edit`, data),
  exportAttendance:(params)  => api.get('/attendance/export', { params, responseType: 'blob' }),
  exportLeaveRequests:(params) => api.get('/attendance/leave/export', { params, responseType: 'blob' }),
};

export const planAPI = {
  getAll:      ()        => api.get('/plans'),
  getById:     (id)      => api.get(`/plans/${id}`),
  getMyPlan:   ()        => api.get('/plans/tenant/my-plan'),
  purchase:    (planId)  => api.post('/plans/tenant/purchase', { planId }),
  requestPlan: (planId)  => api.post('/plans/tenant/purchase', { planId }),
  // superadmin only:
  create:      (data)    => api.post('/plans', data),
  update:      (id, data)=> api.put(`/plans/${id}`, data),
  delete:      (id)      => api.delete(`/plans/${id}`),
  lockTenant:       (tenantId) => api.put(`/plans/lock/${tenantId}`),
};

export const paymentAPI = {
  createOrder:  (planId)  => api.post('/payments/create-order', { planId }),
  verifyPayment:(data)    => api.post('/payments/verify', data),
  getMyPayments:()        => api.get('/payments/my'),
  getAllPayments:(params) => api.get('/payments/all', { params }),  // superadmin
};

export const contactAPI = {
  submit: (data) => api.post('/contact', data),
};

export default api;